import { apiClient } from "@/lib/api-client";
import type { ApiResponse } from "@/types/api";
import type { Room, RoomCreateRequest, RoomUpdateRequest } from "../types";

export async function listRooms(scheduleId: string): Promise<Room[]> {
  const response = await apiClient.get<ApiResponse<Room[]>>(
    `/schedules/${scheduleId}/rooms`,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to load rooms");
}

export async function createRoom(
  scheduleId: string,
  payload: RoomCreateRequest,
): Promise<Room> {
  const response = await apiClient.post<ApiResponse<Room>>(
    `/schedules/${scheduleId}/rooms`,
    payload,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to create room");
}

export async function updateRoom(
  scheduleId: string,
  roomId: string,
  payload: RoomUpdateRequest,
): Promise<Room> {
  const response = await apiClient.put<ApiResponse<Room>>(
    `/schedules/${scheduleId}/rooms/${roomId}`,
    payload,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to update room");
}

export async function deleteRoom(
  scheduleId: string,
  roomId: string,
): Promise<void> {
  const response = await apiClient.delete<ApiResponse<null> | "">(
    `/schedules/${scheduleId}/rooms/${roomId}`,
  );
  // 204 No Content yields an empty body — treat as success.
  if (response.status === 204) return;
  if (typeof response.data === "object" && response.data !== null) {
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to delete room");
    }
  }
}

export async function setRoomAllowedSubjectAreas(
  scheduleId: string,
  roomId: string,
  subjectAreaIds: string[],
): Promise<void> {
  const response = await apiClient.put<ApiResponse<null> | "">(
    `/schedules/${scheduleId}/rooms/${roomId}/allowed-subject-areas`,
    { subjectAreaIds },
  );
  // 204 No Content on success.
  if (response.status === 204) return;
  if (typeof response.data === "object" && response.data !== null) {
    if (!response.data.success) {
      throw new Error(
        response.data.message || "Failed to update allowed subject areas",
      );
    }
  }
}
